import type { CheckoutRemoval } from "./projectCheckoutOptions";

/** The one effect removing a project from an Environment has. */
export interface CheckoutRemovalEffects {
  /**
   * ACCEPTED AND NEVER CALLED, on purpose.
   *
   * It is still in the signature so the existing call sites and their
   * regression tests keep compiling, and so this note sits where anyone
   * tempted to wire it back up will read it. Removing a project must not
   * destroy the Environment hosting it — see below.
   */
  readonly destroyRuntime: (environmentId: string) => Promise<void>;
  /** Drops the project row, and with it the project's threads, on that Environment. */
  readonly removeProject: (environmentId: string, projectId: string) => Promise<void>;
}

/**
 * Remove one project from the Environment it sits on.
 *
 * It does NOT tear the Environment down, and that is the whole point of this
 * function. While a Sprite backed exactly one Project the two were
 * indistinguishable, so removal destroyed the runtime and its volume alongside
 * the project row. Under docs/adr/environment-outlives-sprite.md an Environment
 * hosts MANY Projects, and the same call would delete a machine other Projects
 * are still running on.
 *
 * Ending the Environment lives in Settings → Connections, deliberately
 * somewhere else. Ending one project and ending the machine a person's whole
 * workspace runs on are different decisions, and a decision that destructive
 * should not be reachable from a row about one project.
 *
 * The effect is injected because the alternative way to test this is to delete
 * a real project and its threads, which is not a thing to do to prove a button.
 */
export async function performCheckoutRemoval(
  removal: CheckoutRemoval,
  effects: CheckoutRemovalEffects,
): Promise<void> {
  if (removal.kind !== "removable") return;
  await effects.removeProject(removal.environmentId, removal.projectId);
}
